import React, { useState, useEffect } from "react";
import { FiMessageCircle, FiPlus, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../../constants/constants";

interface ForumTopic {
  id: string;
  title: string;
  content: string;
  author: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
    profileImage?: string;
  };
  createdAt: string;
  updatedAt: string;
  isLocked: boolean;
  isPinned: boolean;
  views: number;
  commentsCount: number;
  category?: {
    id: number;
    name: string;
    description?: string;
  };
}

interface TopicsResponse {
  success: boolean;
  data: {
    topics: ForumTopic[];
    count: number;
  };
}

interface Category {
  id: number;
  name: string;
}

const EtudiantForum: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [topics, setTopics] = useState<ForumTopic[]>([]);
  const [filteredTopics, setFilteredTopics] = useState<ForumTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // États pour la création d'un nouveau sujet
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [isPinned, setIsPinned] = useState(false);
  const [creatingTopic, setCreatingTopic] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Fetch topics from API
  const fetchTopics = async () => {
    setLoading(true);
    try {
      const url = API_ENDPOINTS.FORUM.GET_TOPICS;
      const accessToken = localStorage.getItem("accessToken");
      console.log("Token d'accès:", accessToken ? "Présent" : "Absent");

      // Use built-in fetch API instead of axios
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      console.log("Statut de la réponse HTTP:", response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: TopicsResponse = await response.json();
      console.log("Forum topics data:", data);

      if (data && data.success && data.data && data.data.topics) {
        setTopics(data.data.topics);
        setFilteredTopics(data.data.topics);
      } else {
        setTopics([]);
        setFilteredTopics([]);
      }
      setError(null);
    } catch (err) {
      console.error("Failed to fetch forum topics:", err);
      setError(
        "Une erreur est survenue lors de la récupération des sujets du forum"
      );
      setTopics([]);
      setFilteredTopics([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTopics();
  }, []);

  // Filtrer les sujets en fonction de la recherche
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredTopics(topics);
    } else {
      const query = searchQuery.toLowerCase().trim();
      const filtered = topics.filter(
        (topic) =>
          topic.title.toLowerCase().includes(query) ||
          topic.content.toLowerCase().includes(query)
      );
      setFilteredTopics(filtered);
    }
  }, [searchQuery, topics]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleCreateNewTopic = () => {
    // Récupérer les catégories avant d'ouvrir la modale
    setLoadingCategories(true);
    fetchCategories().then(() => {
      setShowCreateModal(true);
      setLoadingCategories(false);
    });
  };

  const handleTopicClick = (topicId: string) => {
    navigate(`/etudiant/forum/sujet/${topicId}`);
  };

  // Récupérer les catégories de forum disponibles
  const fetchCategories = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.FORUM.GET_CATEGORIES);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data && Array.isArray(data)) {
        setCategories(data);
        if (data.length > 0) {
          setCategoryId(data[0].id);
        }
        return data;
      }
      return [];
    } catch (err) {
      console.error("Failed to fetch forum categories:", err);
      setCreateError("Erreur lors de la récupération des catégories");
      return [];
    }
  };

  const handleSubmitNewTopic = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      setCreateError("Le titre est requis");
      return;
    }

    if (!content.trim()) {
      setCreateError("Le contenu est requis");
      return;
    }

    if (!categoryId) {
      setCreateError("Veuillez sélectionner une catégorie");
      return;
    }

    setCreatingTopic(true);
    setCreateError(null);

    try {
      const response = await fetch(API_ENDPOINTS.FORUM.CREATE_TOPIC, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify({
          title,
          content,
          categoryId,
          isPinned,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        if (response.status === 404) {
          throw new Error("Catégorie non trouvée");
        } else {
          throw new Error(errorData?.message || "Une erreur est survenue");
        }
      }

      // Fermer la modale et réinitialiser le formulaire
      closeModal();

      // Rafraîchir la liste des sujets
      await fetchTopics();
    } catch (err: unknown) {
      console.error("Failed to create forum topic:", err);
      setCreateError(
        err instanceof Error
          ? err.message
          : "Une erreur est survenue lors de la création du sujet"
      );
    } finally {
      setCreatingTopic(false);
    }
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setTitle("");
    setContent("");
    setIsPinned(false);
    setCreateError(null);
  };

  return (
    <div className="bg-[#fefaf6]">
      <div className="container mx-auto py-2 px-4">
        <div className="flex flex-col w-full">
          {/* En-tête Forum et barre de recherche */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
            <h1 className="text-2xl font-bold text-gray-800">Forum</h1>

            <div className="flex items-center w-full md:w-auto">
              <div className="relative w-full md:w-auto">
                <input
                  type="text"
                  placeholder="Rechercher"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600"
                />
              </div>
            </div>
          </div>

          {/* Titre de la section et lien pour créer un nouveau forum */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <h2 className="text-xl font-semibold text-gray-800">
              Sujets de la communauté
              {filteredTopics.length !== topics.length && (
                <span className="ml-2 text-sm text-gray-500">
                  ({filteredTopics.length} résultat
                  {filteredTopics.length !== 1 ? "s" : ""})
                </span>
              )}
            </h2>
            <button
              onClick={handleCreateNewTopic}
              className="text-green-600 hover:text-green-800 font-medium flex items-center gap-2 no-underline transition-colors py-1 px-2 bg-transparent border-0"
            >
              <FiPlus size={18} /> Créer un forum
            </button>
          </div>

          {/* Afficher le message d'erreur */}
          {error && (
            <div className="py-4 px-6 mb-6 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}

          {/* Afficher l'état de chargement */}
          {loading && (
            <div className="text-center py-8">
              <p className="text-gray-600">Chargement des sujets...</p>
            </div>
          )}

          {/* Message si aucun résultat */}
          {!loading && filteredTopics.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-600">
                {error
                  ? "Aucun sujet disponible."
                  : "Aucun sujet ne correspond à votre recherche."}
              </p>
            </div>
          )}

          {/* Liste des sujets */}
          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTopics.map((topic) => (
                <div
                  key={topic.id}
                  className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleTopicClick(topic.id)}
                >
                  <div className="flex items-start space-x-2 mb-3">
                    <div className="text-red-400 mt-1">
                      <FiMessageCircle size={20} />
                    </div>
                    <h3 className="font-medium text-gray-800">{topic.title}</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                    {topic.content}
                  </p>
                  <div className="flex justify-between text-xs text-orange-500">
                    <span>{topic.commentsCount} publications</span>
                    <span>
                      Par {topic.author.firstName} {topic.author.lastName}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal pour créer un nouveau sujet */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-bold text-gray-800">
                Créer un nouveau sujet
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="p-6">
              {createError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {createError}
                </div>
              )}

              <form onSubmit={handleSubmitNewTopic}>
                <div className="mb-4">
                  <label
                    htmlFor="title"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    Titre *
                  </label>
                  <input
                    type="text"
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600"
                    placeholder="Titre de votre sujet"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="category"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    Catégorie *
                  </label>
                  {loadingCategories ? (
                    <p className="text-gray-500">
                      Chargement des catégories...
                    </p>
                  ) : (
                    <select
                      id="category"
                      value={categoryId || ""}
                      onChange={(e) => setCategoryId(Number(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600"
                      required
                    >
                      <option value="" disabled>
                        Sélectionnez une catégorie
                      </option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="content"
                    className="block text-gray-700 font-medium mb-2"
                  >
                    Contenu *
                  </label>
                  <textarea
                    id="content"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600"
                    placeholder="Décrivez votre sujet en détail..."
                    required
                  />
                </div>

                {/* Option pour épingler le sujet si l'utilisateur est un modérateur ou admin */}
                <div className="mb-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isPinned}
                      onChange={(e) => setIsPinned(e.target.checked)}
                      className="mr-2 h-5 w-5 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-gray-700">Épingler ce sujet</span>
                  </label>
                </div>

                <div className="flex items-center justify-end space-x-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={creatingTopic}
                    className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                  >
                    {creatingTopic ? "Création en cours..." : "Créer le sujet"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EtudiantForum;
